import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc,
  deleteDoc,
  doc,
  query, 
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { logger, performance, logFirebaseError } from '../utils/logger';


const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // 🆕 DEBUG : Log des changements d'état
  useEffect(() => {
    logger.log('🔄 [NotificationContext] État mis à jour:', {
      notifications: notifications.length,
      unreadCount: unreadCount,
      isLoading: isLoading
    });
  }, [notifications, unreadCount, isLoading]);

  // Écouter les notifications en temps réel
  useEffect(() => {
    if (!currentUser) {
      logger.log('⏸️ [NotificationContext] Pas d\'utilisateur, reset');
      setNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    logger.log('🔔 NotificationContext: Initialisation listeners pour', currentUser.id);

    // 🆕 CORRECTION : Récupérer toutes les notifications et filtrer côté client
    // car Firestore ne permet pas de requêtes complexes avec array-contains-any sur plusieurs champs
    const notificationsQuery = query(
      collection(db, 'notifications'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      logger.log('📡 [NotificationContext] Snapshot reçu');
      
      // 🆕 CORRECTION : Filtrer les notifications côté client
      const allNotifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        isRead: doc.data().readBy?.includes(currentUser.id) || false
      }));

      // Filtrer pour l'utilisateur actuel
      const notifs = allNotifs.filter(notif => {
        const targetUsers = notif.targetUsers || [];
        const targetRoles = notif.targetRoles || [];
        
        // Vérifier si la notification cible cet utilisateur
        const isTargeted = 
          targetUsers.includes('all') ||                    // Pour tout le monde
          targetUsers.includes(currentUser.id) ||           // Pour cet utilisateur spécifiquement
          targetUsers.includes(currentUser.role) ||         // Pour ce rôle (ancien système)
          targetRoles.includes(currentUser.role);           // Pour ce rôle (nouveau système)
        
        return isTargeted;
      });

      logger.log('🔔 Notifications chargées:', notifs.length, '/', allNotifs.length);
      logger.log('📋 Détail complet des notifications:', notifs);
      
      setNotifications(notifs);
      logger.log('✅ setNotifications appelé avec:', notifs.length, 'notifications');
      
      // Compter les non lues
      const unread = notifs.filter(n => !n.isRead).length;
      logger.log('🔴 Notifications non lues calculées:', unread);
      logger.log('📊 Calcul détaillé:', {
        total: notifs.length,
        lues: notifs.filter(n => n.isRead).length,
        nonLues: unread,
        userId: currentUser.id
      });
      
      logger.log('🎯 Appel setUnreadCount avec:', unread);
      setUnreadCount(unread);
      logger.log('✅ setUnreadCount appelé');
      
      setIsLoading(false);
      logger.log('✅ État notifications mis à jour complètement');
      
    }, (error) => {
      logger.error('❌ Erreur chargement notifications:', error);
      setIsLoading(false);
    });

    return () => {
      logger.log('🔔 NotificationContext: Nettoyage listeners');
      unsubscribe();
    };
  }, [currentUser]);

  // Créer une notification
  const createNotification = async (notificationData) => {
    try {
      logger.log('📝 [NotificationContext] Création notification:', notificationData);
      
      const notification = {
        ...notificationData,
        createdAt: serverTimestamp(),
        readBy: []
      };

      const docRef = await addDoc(collection(db, 'notifications'), notification);
      logger.log('✅ Notification créée:', docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      logger.error('❌ Erreur création notification:', error);
      return { success: false, error: error.message };
    }
  };

  // Marquer une notification comme lue
  const markAsRead = async (notificationId) => {
    if (!currentUser) return;

    try {
      logger.log('📖 [NotificationContext] Marquer comme lue:', notificationId);
      
      await updateDoc(doc(db, 'notifications', notificationId), {
        readBy: arrayUnion(currentUser.id)
      });
      
      logger.log('✅ Notification marquée comme lue:', notificationId);
      return { success: true };
    } catch (error) {
      logger.error('❌ Erreur marquage notification:', error);
      return { success: false, error: error.message };
    }
  };

  // Marquer toutes comme lues
  const markAllAsRead = async () => {
    if (!currentUser) return;

    try {
      logger.log('📖 [NotificationContext] Marquer TOUTES comme lues');
      
      const unreadNotifs = notifications.filter(n => !n.isRead);
      logger.log(`📊 ${unreadNotifs.length} notification(s) à marquer comme lues`);
      
      const promises = unreadNotifs.map(notif => {
        logger.log('📖 Marquage:', notif.id);
        return updateDoc(doc(db, 'notifications', notif.id), {
          readBy: arrayUnion(currentUser.id)
        });
      });

      await Promise.all(promises);
      logger.log('✅ Toutes les notifications marquées comme lues');
      return { success: true };
    } catch (error) {
      logger.error('❌ Erreur marquage toutes notifications:', error);
      return { success: false, error: error.message };
    }
  };

  // 🆕 OPTION C : Supprimer une notification
  const deleteNotification = async (notificationId) => {
    try {
      logger.log('🗑️ [NotificationContext] Suppression notification:', notificationId);
      
      await deleteDoc(doc(db, 'notifications', notificationId));
      
      logger.log('✅ Notification supprimée:', notificationId);
      return { success: true };
    } catch (error) {
      logger.error('❌ Erreur suppression notification:', error);
      return { success: false, error: error.message };
    }
  };

  // 🆕 OPTION C : Supprimer plusieurs notifications par critères
  const deleteNotificationsByUserId = async (userId) => {
    try {
      logger.log('🗑️ [NotificationContext] Recherche notifications pour userId:', userId);
      
      // Trouver toutes les notifications liées à cet utilisateur
      const relatedNotifs = notifications.filter(n => 
        (n.type === 'approval' && n.data?.userId === userId) ||
        (n.link?.includes(`user/${userId}`))
      );

      logger.log(`🗑️ ${relatedNotifs.length} notification(s) trouvée(s) pour suppression`);
      logger.log('📋 Notifications à supprimer:', relatedNotifs);

      // Supprimer toutes les notifications trouvées
      const promises = relatedNotifs.map(notif => {
        logger.log('🗑️ Suppression:', notif.id);
        return deleteDoc(doc(db, 'notifications', notif.id));
      });

      await Promise.all(promises);
      logger.log('✅ Notifications supprimées:', relatedNotifs.length);
      return { success: true, count: relatedNotifs.length };
    } catch (error) {
      logger.error('❌ Erreur suppression notifications par userId:', error);
      return { success: false, error: error.message };
    }
  };

  // Notifications par type
  const getNotificationsByType = (type) => {
    const filtered = notifications.filter(n => n.type === type);
    logger.log(`🔍 [NotificationContext] Notifications type '${type}':`, filtered.length);
    return filtered;
  };

  const value = {
    notifications,
    unreadCount,
    isLoading,
    createNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteNotificationsByUserId,
    getNotificationsByType
  };

  logger.log('🎁 [NotificationContext] Valeur exportée:', {
    notificationsCount: notifications.length,
    unreadCount: unreadCount,
    isLoading: isLoading
  });

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;